import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import { useTelegram } from '../context/TelegramContext';

// Стилизованные компоненты
const Container = styled.div`
  padding: 16px;
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #4a6fa1;
  margin: 0;
`;

const BackButton = styled(Link)`
  display: inline-block;
  padding: 8px 16px;
  background: #f0f3f9;
  color: #4a6fa1;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.05),
              -2px -2px 5px rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
  
  &:hover, &:focus {
    background: #e6ebf5;
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1),
                -1px -1px 3px rgba(255, 255, 255, 0.9);
  }
  
  &:active {
    box-shadow: inset 1px 1px 3px rgba(0, 0, 0, 0.1),
                inset -1px -1px 3px rgba(255, 255, 255, 0.9);
  }
`;

const Tabs = styled.div`
  display: flex;
  margin-bottom: 20px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.05),
              -2px -2px 5px rgba(255, 255, 255, 0.8);
`;

const TabButton = styled.button`
  flex: 1;
  background: ${props => props.$active ? 'linear-gradient(145deg, #4a6fa1, #5d82b3)' : '#f0f3f9'};
  color: ${props => props.$active ? '#ffffff' : '#4a6fa1'};
  border: none;
  padding: 12px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover, &:focus {
    background: ${props => props.$active ? 'linear-gradient(145deg, #4a6fa1, #5d82b3)' : '#e6ebf5'};
  }
  
  &:active {
    box-shadow: inset 1px 1px 3px rgba(0, 0, 0, 0.1),
                inset -1px -1px 3px rgba(255, 255, 255, 0.9);
  }
`;

const CategoriesList = styled.div`
  margin-bottom: 20px;
  border-radius: 10px;
  box-shadow: inset 2px 2px 5px rgba(0, 0, 0, 0.05),
              inset -2px -2px 5px rgba(255, 255, 255, 0.8);
  padding: 10px 0;
  max-height: 400px;
  overflow-y: auto;
`;

const CategoryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  margin: 6px 10px;
  background: #f7f9fc;
  border-radius: 8px;
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.05),
              -1px -1px 3px rgba(255, 255, 255, 0.8);
  
  span {
    color: #333;
    font-weight: 500;
  }
  
  button {
    background: #ff5c5c;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.2s ease;
    
    &:hover, &:focus {
      background: #e53e3e;
    }
  }
`;

const AddCategory = styled.div`
  display: flex;
  margin-top: 20px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.05),
              -2px -2px 5px rgba(255, 255, 255, 0.8);
  transition: transform 0.3s ease;
  
  input {
    flex: 1;
    padding: 12px 16px;
    border: none;
    background: #f0f3f9;
    color: #333;
    font-size: 16px;
    
    &:focus {
      outline: none;
      background: #e6ebf5;
    }
    
    &::placeholder {
      color: #8da2c0;
    }
  }
  
  button {
    background: linear-gradient(145deg, #4a6fa1, #5d82b3);
    color: white;
    border: none;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;
    
    &:hover, &:focus {
      background: linear-gradient(145deg, #4263a1, #5275b3);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 30px 0;
  color: #8da2c0;
  font-size: 16px;
`;

const ErrorMessage = styled.div`
  background: #fff5f5;
  color: #e53e3e;
  padding: 10px;
  border-radius: 8px;
  margin-bottom: 15px;
  text-align: center;
`;

const Categories = () => {
  const { t } = useTranslation();
  const { telegramUserId } = useTelegram();
  const [currentType, setCurrentType] = useState('income');
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const addCategoryRef = useRef(null);
  const isMountedRef = useRef(true);

  // Загрузка категорий
  const loadCategories = async (type) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`https://fftg.farodinprojects.org:3000/categories?user_id=${telegramUserId}&type=${type}`);
      
      if (!response.ok) {
        throw new Error(t('category_load_error'));
      }
      
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
      setError(t('category_load_error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Добавление категории
  const addCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    
    try {
      setIsLoading(true);
      setError('');
      
      const response = await fetch('https://fftg.farodinprojects.org:3000/add_category', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: telegramUserId, 
          name, 
          type: currentType 
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(t('category_add_error'));
      }
      
      setNewCategoryName('');
      await loadCategories(currentType);
    } catch (error) {
      console.error('Error adding category:', error);
      setError(t('category_add_error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Удаление категории
  const deleteCategory = async (name) => {
    if (!window.confirm(t('delete_confirmation'))) return;
    
    try {
      setIsLoading(true);
      setError('');
      
      const response = await fetch('https://fftg.farodinprojects.org:3000/delete_category', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: telegramUserId, 
          name, 
          type: currentType 
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(t('category_delete_error'));
      }
      
      await loadCategories(currentType);
    } catch (error) {
      console.error('Error deleting category:', error);
      setError(t('category_delete_error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Обработка нажатия Enter в поле ввода
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      addCategory();
    }
  };

  // Обработка изменения типа категорий
  const handleTypeChange = (type) => {
    setCurrentType(type);
    loadCategories(type);
  };

  // Адаптация для мобильной клавиатуры
  useEffect(() => {
    const adjustAddCategoryOnKeyboard = () => {
      if (!addCategoryRef.current || !isMountedRef.current) return;
      
      const visualViewport = window.visualViewport;
      
      if (visualViewport) {
        const handleResize = () => {
          if (!addCategoryRef.current || !isMountedRef.current) return;
          
          const isKeyboardOpen = visualViewport.height < window.innerHeight;
          addCategoryRef.current.style.transform = isKeyboardOpen ? 'translateY(-50px)' : '';
        };
        
        visualViewport.addEventListener('resize', handleResize);
        
        return () => {
          visualViewport.removeEventListener('resize', handleResize);
        };
      }
    };
    
    adjustAddCategoryOnKeyboard();
    
    // Устанавливаем флаг при размонтировании
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Загрузка категорий при первом рендере
  useEffect(() => {
    if (telegramUserId) {
      loadCategories(currentType);
    }
  }, [telegramUserId]);

  return (
    <Layout>
      <Container>
        <PageHeader>
          <PageTitle>{t('manage_categories')}</PageTitle>
          <BackButton to="/settings">{t('back')}</BackButton>
        </PageHeader>
        <Tabs>
          <TabButton 
            $active={currentType === 'income'} 
            onClick={() => handleTypeChange('income')}
          >
            {t('income')}
          </TabButton>
          <TabButton 
            $active={currentType === 'expense'} 
            onClick={() => handleTypeChange('expense')}
          >
            {t('expenses')}
          </TabButton>
        </Tabs>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <CategoriesList>
          {isLoading ? (
            <EmptyState>{t('loading')}</EmptyState>
          ) : categories.length > 0 ? (
            categories.map((category, index) => (
              <CategoryItem key={`${category.name}-${index}`}>
                <span>{category.name}</span>
                <button onClick={() => deleteCategory(category.name)}>
                  {t('delete')}
                </button>
              </CategoryItem>
            ))
          ) : (
            <EmptyState>{t('no_categories')}</EmptyState>
          )}
        </CategoriesList>
        
        <AddCategory ref={addCategoryRef}>
          <input
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('category_placeholder')}
          />
          <button onClick={addCategory}>
            {t('add')}
          </button>
        </AddCategory>
      </Container>
    </Layout>
  );
};

export default Categories;